import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Critical pages loaded eagerly (above-the-fold, SEO)
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";

// Lazy-loaded pages
const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const B2BPage = lazy(() => import("@/pages/B2BPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const AreasPage = lazy(() => import("@/pages/AreasPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const ClientDashboard = lazy(() => import("@/pages/ClientDashboard"));
const KarigarDashboard = lazy(() => import("@/pages/KarigarDashboard"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const TrackOrderPage = lazy(() => import("@/pages/TrackOrderPage"));
const DesignerShowcasePage = lazy(() => import("@/pages/DesignerShowcasePage"));
// New SEO pages
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const StartYourProjectPage = lazy(() => import("@/pages/StartYourProjectPage"));
const PricingGuidePage = lazy(() => import("@/pages/PricingGuidePage"));
const ForDesignersPage = lazy(() => import("@/pages/ForDesignersPage"));
const CustomBridalLehengasPage = lazy(() => import("@/pages/CustomBridalLehengasPage"));
const CustomBlousesDupattasPage = lazy(() => import("@/pages/CustomBlousesDupattasPage"));
const InternationalOrdersPage = lazy(() => import("@/pages/InternationalOrdersPage"));
const MeetTheKarigarsPage = lazy(() => import("@/pages/MeetTheKarigarsPage"));
const BehindTheCraftPage = lazy(() => import("@/pages/BehindTheCraftPage"));
const GlobalClientsPage = lazy(() => import("@/pages/GlobalClientsPage"));
const StartYourCollectionPage = lazy(() => import("@/pages/StartYourCollectionPage"));
const GetYourLehengaPage = lazy(() => import("@/pages/GetYourLehengaPage"));
const LehengaInspirationPage = lazy(() => import("@/pages/LehengaInspirationPage"));
const EstimateCalculatorPage = lazy(() => import("@/pages/EstimateCalculatorPage"));
const WeddingPlannerGuidePage = lazy(() => import("@/pages/WeddingPlannerGuidePage"));
const ReferralTrackPage = lazy(() => import("@/pages/ReferralTrackPage"));
const EmbroideryTechniquePage = lazy(() => import("@/pages/EmbroideryTechniquePage"));

import WhatsAppButton from "@/components/WhatsAppButton";

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#F9F9F7]">
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/b2b" element={<B2BPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/legal/:page" element={<LegalPage />} />
            <Route path="/areas" element={<AreasPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/track" element={<TrackOrderPage />} />
            <Route path="/designers" element={<DesignerShowcasePage />} />
            {/* New SEO pages */}
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/start-your-project" element={<StartYourProjectPage />} />
            <Route path="/pricing-guide" element={<PricingGuidePage />} />
            <Route path="/for-designers" element={<ForDesignersPage />} />
            <Route path="/custom-bridal-lehengas" element={<CustomBridalLehengasPage />} />
            <Route path="/custom-blouses-dupattas" element={<CustomBlousesDupattasPage />} />
            <Route path="/international-orders" element={<InternationalOrdersPage />} />
            <Route path="/meet-the-karigars" element={<MeetTheKarigarsPage />} />
            <Route path="/behind-the-craft" element={<BehindTheCraftPage />} />
            <Route path="/global-clients" element={<GlobalClientsPage />} />
            <Route path="/start-your-collection" element={<StartYourCollectionPage />} />
            <Route path="/get-your-lehenga" element={<GetYourLehengaPage />} />
            <Route path="/lehenga-inspiration" element={<LehengaInspirationPage />} />
            <Route path="/estimate" element={<EstimateCalculatorPage />} />
            <Route path="/wedding-planner-guide" element={<WeddingPlannerGuidePage />} />
            <Route path="/referral-track" element={<ReferralTrackPage />} />
            <Route path="/embroidery/:technique" element={<EmbroideryTechniquePage />} />
            {/* Auth & Portals */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<ClientDashboard />} />
            <Route path="/karigar-dashboard" element={<KarigarDashboard />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
        </Suspense>
        <WhatsAppButton />
        <Toaster position="top-right" />
      </BrowserRouter>
      <div className="noise-overlay" aria-hidden="true" />
    </div>
  );
}

export default App;
